<template>
  <div v-if="visible" class="form-overlay">
    <div class="form-container">
      <button class="close-btn" @click="closeForm">&times;</button>
      <h2>{{ orderId ? '編輯訂單' : '新增訂單' }}</h2>
      <form @submit.prevent="submitForm">
        <div class="form-sections">
          <!-- Order Information Section -->
          
          <div class="form-section order-information">
            <h3>基本資料</h3>  
            <div class="form-row">
              <div class="form-group" :class="{ 'has-error': (submitted && !formData.id) || orderIdInvalid }">
                <label for="id">訂單編號</label>
                <input 
                  class="info-column"
                  type="text" 
                  id="id" 
                  v-model="formData.id" 
                  required 
                  pattern="[A-Za-z0-9-]+"
                  title="只能輸入英文字母、數字與 -"
                  placeholder="請輸入訂單編號"
                  @blur="checkOrderId"
                >
                <span v-if="submitted && !formData.id" class="error-message">訂單編號是必填的</span>
                <span v-if="orderIdInvalid" class="error-message">{{ orderIdErrorMessage }}</span>
                <span v-if="orderIdExists && !orderId" class="error-message">訂單編號已存在</span>
              </div>
              <div class="form-group" :class="{ 'has-error': submitted && !formData.name }">
                <label for="name">顧客姓名</label>
                <input 
                  class="info-column"
                  type="text" 
                  id="name" 
                  v-model="formData.name" 
                  required
                  placeholder="請輸入顧客姓名"
                >
                <span v-if="submitted && !formData.name" class="error-message">顧客姓名是必填的</span>
              </div>
              <div class="form-group" :class="{ 'has-error': submitted && !formData.sales_id }">
                <label for="sales_id">銷售員</label>
                <select 
                  class="info-column"
                  id="sales_id" 
                  v-model="formData.sales_id" 
                  required
                >
                  <option value="">選擇銷售員</option>
                  <option v-for="sales in salesList" :key="sales.id" :value="sales.id">
                    {{ sales.name }}
                  </option>
                </select>
                <span v-if="submitted && !formData.sales_id" class="error-message">銷售員是必填的</span>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group" :class="{ 'has-error': submitted && !formData.order_date }">
                <label for="order_date">訂貨日期</label>
                <input
                  class="date-column"
                  type="date" 
                  id="order_date" 
                  v-model="formData.order_date" 
                  required
                >
                <span v-if="submitted && !formData.order_date" class="error-message">訂貨日期是必填的</span>
              </div>
              <div class="form-group">
                <label for="deliver_date">到貨日期</label>
                <input class="date-column" type="date" id="deliver_date" v-model="formData.deliver_date">
              </div>
              <div class="form-group">
                <label for="intro_by">來源</label>
                <input
                  class="info-column" 
                  type="text" 
                  id="intro_by"
                  v-model="formData.intro_by"
                  list="introByOptionsList"
                  placeholder="請選擇或輸入來源"
                >
                <datalist id="introByOptionsList">
                  <option v-for="source in introByOptions" :key="source.id" :value="source.name">
                    {{ source.name }}
                  </option>
                </datalist>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group" :class="{ 'has-error': submitted && !formData.phone }">
                <label for="phone">聯絡電話</label>
                <input 
                  type="tel" 
                  id="phone" 
                  v-model="formData.phone" 
                  required
                >
                <span v-if="submitted && !formData.phone" class="error-message">聯絡電話是必填的</span>
              </div>
              <div class="form-group address-autocomplete-container" :class="{ 'has-error': submitted && !formData.addr }">
                <label for="address">地址</label>
                <input 
                  type="text"
                  id="address" 
                  v-model="formData.addr" 
                  required 
                  placeholder="請輸入地址"
                  @input="handleAddressInput('addr')"
                  @focus="showSuggestions.addr = true"
                  @blur="hideSuggestions('addr')"
                >
                <ul v-if="showSuggestions.addr && addressSuggestions.addr && addressSuggestions.addr.length" class="address-suggestions-list">
                  <li 
                    v-for="suggestion in addressSuggestions.addr" 
                    :key="suggestion.place_id" 
                    @mousedown.prevent="selectAddressSuggestion('addr', suggestion)"
                  >
                    {{ suggestion.description }}
                  </li>
                </ul>
                <span v-if="submitted && !formData.addr" class="error-message">地址是必填的</span>
              </div>
              <div class="form-group">
                <label for="memo">備註</label>
                <textarea id="memo" v-model="formData.memo" rows="2"></textarea>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label for="phone2">聯絡電話2</label>
                <input type="tel" id="phone2" v-model="formData.phone2">
              </div>
              <div class="form-group address-autocomplete-container">
                <label for="address2">地址2</label>
                <input 
                  type="text"
                  id="address2" 
                  v-model="formData.addr2" 
                  placeholder="請輸入地址2"
                  @input="handleAddressInput('addr2')"
                  @focus="showSuggestions.addr2 = true"
                  @blur="hideSuggestions('addr2')"
                >
                <ul v-if="showSuggestions.addr2 && addressSuggestions.addr2 && addressSuggestions.addr2.length" class="address-suggestions-list">
                  <li 
                    v-for="suggestion in addressSuggestions.addr2" 
                    :key="suggestion.place_id" 
                    @mousedown.prevent="selectAddressSuggestion('addr2', suggestion)"
                  >
                    {{ suggestion.description }}
                  </li>
                </ul>
              </div>
              <div v-if="formData.need_invoice" class="form-group">
                <label for="invoice">發票號碼</label>
                <input type="text" id="invoice" v-model="formData.invoice">
              </div>
            </div>
            <div class="checkbox-group">
              <div class="checkbox-item">
                <input type="checkbox" id="delivered" v-model="formData.delivered">
                <label for="delivered">已交貨</label>
              </div>
              <div class="checkbox-item">
                <input type="checkbox" id="share_day" v-model="formData.share_day">
                <label for="share_day">節假日</label>
              </div>
              <div class="checkbox-item">
                <input 
                  type="checkbox" 
                  id="need_invoice" 
                  v-model="formData.need_invoice"
                  @change="handleNeedInvoiceChange"
                >
                <label for="need_invoice">開發票</label>
              </div>
            </div>
          </div>

          <!-- Add Product Section -->
          <div class="form-section add-product">
            <h3>訂購商品資訊</h3>
            <div class="product-list-container">
              <table class="product-list">
                <thead>
                  <tr>
                    <!-- Product -->
                    <th>品項</th>
                    <th>尺寸</th>
                    <th>單價</th>
                    <th>數量</th>
                    <th>布色</th>
                    <th>特殊</th>
                    <th>特殊加價</th>
                    <th>特殊成本</th>
                    <th>折扣</th>
                    <th>備註</th>
                    <th>小計</th>
                    <!-- Shipment -->
                    <th>司機</th>
                    <th>運費</th>
                    <th>運費加價</th>
                    <th>運費備註</th>
                    <!-- Total -->
                    <th>小計</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Existing Order Items -->
                  <tr v-for="(item, index) in orderItems" :key="index">
                    <!-- Product -->
                    <td>{{ item.product_name }}</td>
                    <td>{{ item.size }}</td>
                    <td>{{ productList.find(p => p.id === item.product_id)?.price }}</td>
                    <td>{{ item.quantity }}</td>
                    <td>{{ item.color || '-' }}</td>
                    <td>{{ item.customized ? 'Yes' : 'No' }}</td>
                    <td>{{ formatCurrency(item.extra_fee || 0) }}</td>
                    <td>{{ formatCurrency(item.extra_cost || 0) }}</td>
                    <td>{{ formatCurrency(item.discount || 0) }}</td>
                    <td>{{ item.extra_memo || '' }}</td>
                    <td>{{ formatCurrency(item.sum || 0) }}</td>
                    <!-- shipment -->
                    <td>{{ driverList.find(d => d.id === item.ship.driver_id)?.name || item.ship.driver_id }}</td>
                    <td>{{ formatCurrency(item.ship.fee || 0) }}</td>
                    <td>{{ formatCurrency(item.ship.extra_fee || 0) }}</td>
                    <td>{{ item.ship.extra_memo || '' }}</td>
                    <!-- Total -->
                    <td>{{ formatCurrency(item.ship.sum || 0) }}</td>
                    <td>
                      <button 
                        type="button" 
                        class="remove-btn" 
                        @click="removeOrderItem(index, 'order_item')"
                      >
                        -
                      </button>
                    </td>
                  </tr>
                  
                  <!-- New Product Input Row -->
                  <tr class="new-product-input">
                    <td class="product-name-column">
                      <!-- Product -->
                       <div class="product-name-flex-container">
                        <select v-model="currentOrderItem.product_name">
                        <option value="">選擇品項</option>
                        <option 
                          v-for="name in productList.map(p => p.name).filter((val, idx, array) => array.indexOf(val) === idx)" 
                          :key="name" 
                          :value="name"
                        >
                          {{ name }}
                        </option>
                        </select>
                        <div v-if="giftList.includes(currentOrderItem.product_name)" class="gift-colmun">
                          <input class="checkbox-gift" 
                            type="checkbox" 
                            id="checkbox-gift"
                            v-model="currentOrderItem.is_gift"
                            @change="handleGiftCheckboxChange"
                          >
                          <label for="checkbox-gift">贈品</label>
                        </div>
                       </div>
                    </td>
                    <td class="size-column">
                      <select 
                        v-model="currentOrderItem.size"
                        class="full-width"
                        :disabled="!currentOrderItem.product_name"
                      >
                        <option 
                          v-for="product in productList.filter(p => p.name === currentOrderItem.product_name)"
                          :key="product.size" 
                          :value="product.size"
                        >
                          {{ product.size }}
                        </option>
                      </select>
                    </td>
                    <td class="price-column">
                      {{ formatCurrency(productList.find(p => p.id === currentOrderItem.product_id)?.price) }}
                    </td>
                    <td class="quantity-column">
                      <input 
                        type="number" 
                        v-model.number="currentOrderItem.quantity" 
                        min="1"
                      >
                    </td>
                    <td class="name-column">
                      <input 
                        type="text" 
                        v-model="currentOrderItem.color"
                        list="colorOptions"
                        class="full-width"
                      >
                      <datalist id="colorOptions">
                        <option v-for="color in colorList" :key="color.name" :value="color.name">
                          {{ color.name }}
                        </option>
                      </datalist>
                    </td>
                    <td class="customize-column">
                      <input 
                        type="checkbox" 
                        v-model="currentOrderItem.customized"
                        class="full-width"
                      >
                    </td>
                    <td class="price-column">
                      <input 
                        type="number" 
                        v-model.number="currentOrderItem.extra_fee" 
                        min="0"
                        data-cy="new-item-extra-fee"
                      >
                    </td>
                    <td class="price-column">
                      <input 
                        type="number" 
                        v-model.number="currentOrderItem.extra_cost" 
                        min="0"
                        data-cy="new-item-extra-cost"
                      >
                    </td>
                    <td class="price-column">
                      <input 
                        type="number" 
                        v-model.number="currentOrderItem.discount" 
                        min="0"
                        :max="maxDiscount"
                        data-cy="new-item-discount"
                      >
                    </td>
                    <td>
                      <input 
                        class="memo-column"
                        type="text"
                        placeholder="備註"
                        v-model="currentOrderItem.extra_memo" 
                        data-cy="new-item-extra-memo"
                      >
                    </td>
                    <td>
                      <span>
                        {{ formatCurrency(currentOrderItem.sum) }}
                      </span>
                    </td>
                    <!-- shipment -->
                    <td>
                      <select 
                        v-model="currentOrderItem.ship.driver_id"
                        class="name-column"
                        data-cy="new-item-ship-driver"
                      >
                        <option 
                          v-for="driver in driverList" 
                          :key="driver.id" 
                          :value="driver.id"
                        >
                          {{ driver.name }}
                        </option>
                      </select>
                    </td>
                    <td class="price-column">
                      <input 
                        type="number" 
                        v-model.number="currentOrderItem.ship.fee"
                        step="1"
                        min="0"
                        @input="updateShipFee($event)"
                        @blur="shipFeeCleaned"
                        data-cy="new-item-ship-fee"
                      >
                    </td>
                    <td class="price-column">
                      <input 
                        type="number" 
                        v-model.number="currentOrderItem.ship.extra_fee" 
                        min="0"
                        data-cy="new-item-ship-extra-fee"
                      >
                    </td>
                    <td>
                      <input
                        class="memo-column"
                        type="text" 
                        v-model="currentOrderItem.ship.extra_memo" 
                        data-cy="new-item-ship-extra-memo"
                      >
                    </td>
                    <!-- Total -->
                    <td>
                      <span v-if="currentOrderItem.ship.driver_id">
                        {{ formatCurrency(currentOrderItem.ship.sum) }}
                      </span>
                    </td>
                    <td class="add-btn-column">
                      <button 
                        type="button" 
                        class="add-btn" 
                        @click="addOrderItem"
                        :disabled="!isValidOrderItem"
                      >
                        +
                      </button>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="total-row">
                    <td colspan="10" class="total-label">訂單總計</td>
                    <td class="total-value">
                      {{ formatCurrency(orderItems.reduce((sum, item) => sum + (item.sum || 0), 0)) }}
                    </td>
                    <td colspan="4" class="total-ship-label">運費總計</td>
                    <td class="total-ship-value">
                      {{ formatCurrency(orderItems.reduce((sum, item) => sum + (item.ship.sum || 0), 0)) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <!-- Disposal and Payments Container -->
          <div class="sections-container">
            <!-- Add Disposal Section -->
            <div class="form-section add-disposal">
              <h3>回收處理</h3>
              <div class="product-list-container">
                <table class="product-list">
                <thead>
                  <tr>
                    <th>數量</th>
                    <th>費用</th>
                    <th>胖嘟嘟</th>
                    <th>小計</th>
                    <th>司機</th>
                    <th>運費</th>
                    <th>運費加價</th>
                    <th>運費備註</th>
                    <th>運費</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Existing Disposal Items -->
                  <tr v-for="(item, index) in disposals" :key="'disposal-' + index">
                    <td>{{ item.quantity }}</td>
                    <td>{{ formatCurrency(item.fee) }}</td>
                    <td>{{ formatCurrency(item.extra_fee) }}</td>
                    <td>{{ formatCurrency(item.sum) }}</td>
                    <td>{{ driverList.find(d => d.id === item.ship.driver_id)?.name || item.ship.driver_id }}</td>
                    <td>{{ formatCurrency(item.ship.fee || 0) }}</td>
                    <td>{{ formatCurrency(item.ship.extra_fee || 0) }}</td>
                    <td>{{ item.ship.extra_memo || '' }}</td>
                    <td>{{ formatCurrency(item.ship ? item.ship.sum : 0) }}</td>
                    <td>
                      <button 
                        type="button" 
                        class="remove-btn" 
                        @click="removeOrderItem(index, 'disposal')"
                      >
                        -
                      </button>
                    </td>
                  </tr>
                  
                  <!-- New Disposal Input Row -->
                  <tr class="new-disposal-input">
                    <td class="quantity-column">
                      <input 
                        type="number" 
                        v-model.number="currentDisposal.quantity" 
                        min="0" 
                        step="1"
                      >
                    </td>
                    <td class="price-column">
                      <input 
                        type="number" 
                        v-model.number="currentDisposal.fee" 
                        min="0"
                      >
                    </td>
                    <td class="price-column">
                      <input 
                        type="number" 
                        v-model.number="currentDisposal.extra_fee" 
                        min="0"
                      >
                    </td>
                    <td class="full-width">{{ formatCurrency(calculateDisposalSum()) }}</td>
                    <td class="name-column">
                      <select 
                        v-model="currentDisposal.ship.driver_id"
                        data-cy="new-disposal-ship-driver"
                      >
                        <option v-for="driver in driverList" :key="driver.id" :value="driver.id">
                          {{ driver.name }}
                        </option>
                      </select>
                    </td>
                    <td class="price-column">
                      <input 
                        type="number" 
                        v-model.number="currentDisposal.ship.fee" 
                        min="0"
                        data-cy="new-disposal-ship-fee"
                      >
                    </td>
                    <td class="price-column">
                      <input 
                        type="number" 
                        v-model.number="currentDisposal.ship.extra_fee" 
                        min="0"
                        data-cy="new-disposal-ship-extra-fee"
                      >
                    </td>
                    <td class="memo-column">
                      <input 
                        type="text" 
                        v-model="currentDisposal.ship.extra_memo" 
                        placeholder="備註"
                        data-cy="new-disposal-ship-extra-memo"
                      >
                    </td>
                    <td class="full-width">{{ formatCurrency(calculateDisposalShipSum()) }}</td>
                    <td class="add-btn-column">
                      <button 
                        type="button" 
                        class="add-btn" 
                        @click="addDisposalItem"
                        :disabled="!currentDisposal.quantity"
                      >
                        +
                      </button>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="total-row">
                    <td colspan="3" class="total-label">回收總計</td>
                    <td class="total-value">
                      {{ formatCurrency(disposals.reduce((sum, item) => sum + (item.sum || 0), 0)) }}
                    </td>
                    <td colspan="4" class="total-label">運費總計</td>
                    <td class="total-value">
                      {{ formatCurrency(disposals.reduce((sum, item) => sum + (item.ship.sum || 0), 0)) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <!-- Payment Section -->
          <div class="form-section add-payment">
            <h3>付款資訊</h3>
            <div class="product-list-container">
              <table class="product-list">
                <thead>
                  <tr>
                    <th>付款日期</th>
                    <th>付款方式</th>
                    <th>金額</th>
                    <th>發票號碼</th>
                    <th>刷卡序號</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Existing Payment Items -->
                  <tr v-for="(item, index) in payments" :key="'payment-' + index">
                    <td>{{ item.pay_date }}</td>
                    <td>{{ paymentMethods.find(p => p.id === item.method_id)?.name }}</td>
                    <td>{{ formatCurrency(item.amount) }}</td>
                    <td>{{ item.invoice }}</td>
                    <td>{{ item.series }}</td>
                    <td>
                      <button 
                        type="button" 
                        class="remove-btn" 
                        @click="removeOrderItem(index, 'payment')"
                      >
                        -
                      </button>
                    </td>
                  </tr>
                  
                  <!-- New Payment Input Row -->
                  <tr class="new-payment-input">
                    <td class="date-column">
                      <input 
                        type="date" 
                        v-model="currentPayment.pay_date" 
                      >
                    </td>
                    <td class="payment-method-column">
                      <select 
                        v-model="currentPayment.method_id"
                        data-cy="new-payment-method"
                      >
                        <option v-for="method in paymentMethods" :key="method.id" :value="method.id">
                          {{ method.name }}
                        </option>
                      </select>
                    </td>
                    <td class="price-column">
                      <input 
                        type="number" 
                        v-model.number="currentPayment.amount" 
                        min="0"
                        data-cy="new-payment-amount"
                      >
                    </td>
                    <td>
                      <input 
                        type="text" 
                        v-model="currentPayment.invoice"
                        class="e-emall"
                        data-cy="new-payment-invoice"
                      >
                    </td>
                    <td>
                      <input 
                        type="text"
                        v-model="currentPayment.series"
                        class="e-small"
                        data-cy="new-payment-series"
                      >
                    </td>
                    <td class="add-btn-column">
                      <button 
                        type="button" 
                        class="add-btn" 
                        @click="addPaymentItem"
                        :disabled="!currentPayment.amount"
                      >
                        +
                      </button>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="total-row">
                    <td colspan="2" class="total-label">已付</td>
                    <td class="total-value">
                      {{ formatCurrency(payments.reduce((sum, item) => sum + (item.amount || 0), 0)) }}
                    </td>
                    <td colspan="0" class="total-label">未付</td>
                    <td class="total-value">
                      {{ formatCurrency(calculateUnpaid()) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
        </div>

        <div class="form-actions">
          <button type="button" class="cancel-btn" @click="closeForm">取消</button>
          <button type="submit" class="submit-btn" :disabled="isSubmitting">
            {{ isSubmitting ? '傳送中...' : (this.orderId ? "修改" : '建立') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
const ORDER_ID_PATTERN = /^[A-Za-z0-9-]+$/;
const ORDER_ID_ERROR = '訂單編號只能包含英文字母、數字與 -';

export default {
  emits: ['close', 'order-updated', 'order-created'],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    orderId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      salesList: [],
      driverList: [],
      introByOptions: [],
      productList: [],
      giftList: [],
      colorList: [],
      paymentMethods: [],
      defaultDriver: null,
      isSubmitting: false,
      submitted: false,
      addressSuggestions: {}, // Use an object to store suggestions for different fields
      showSuggestions: {}, // Use an object to store visibility for different fields
      debounceTimeout: {}, // Use an object to store timeouts for different fields
      creditCardMethodId: 2, // To store the ID of the credit card payment method
      orderIdExists: false,
      orderIdErrorMessage: ORDER_ID_ERROR,
      existingOrderDetails: null,
      formData: {
        id: '', // Required order ID
        name: '',
        sales_id: '',
        order_date: new Date().toISOString().split('T')[0],
        deliver_date: '',
        phone: '',
        phone2: '',
        addr: '',
        addr2: '',
        intro_by: '',
        memo: '',
        need_invoice: true,
        invoice: '',
        share_day: false
      },
      orderItems: [],
      disposals: [],
      payments: [],
      currentOrderItem: {
        product_id: '',
        size: '',
        quantity: 1,
        color: '',
        customized: false,
        extra_memo: '',
        ship: {
          driver_id: '',
          share_percent: 0,
          is_fixed_fee: false,
          fee: 0,
          extra_fee: 0,
          extra_memo: '',
          sum: 0
        }
      },
      currentDisposal: {
        quantity: 0,
        fee: 350,
        extra_fee: 0,
        ship: {
          driver_id: '',
          is_fixed_fee: true,
          fee: 100,
          extra_fee: 0,
          extra_memo: '',
          sum: 0
        },
        sum: 350
      },
      currentPayment: {
        pay_date: new Date().toISOString().split('T')[0],
        method_id: '',
        amount: 0,
        invoice: '',
        series: ''
      },
      productLoading: false,
      productError: null,
    };
  },
  computed: {
    // Order id is used as a URL path segment (/api/orders/<order_id>), a character
    // like '/' would break every following request on that order.
    orderIdInvalid() {
      return !!this.formData.id && !ORDER_ID_PATTERN.test(this.formData.id);
    },
    isValidOrderItem() {
      return this.currentOrderItem.product_id && this.currentOrderItem.quantity > 0;
    },
    maxDiscount() {
      const selectedProduct = this.productList.find(
        p => p.id === this.currentOrderItem.product_id
      );
      const extra_fee = parseFloat(this.currentOrderItem.extra_fee) || 0;
      return selectedProduct ? (selectedProduct.price + extra_fee) : 0;
    },
    isNeedInvoiceDisabled() {
      // Only disable if a credit card payment exists
      return this.payments.some(payment => payment.method_id === this.creditCardMethodId);
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.fetchSalesList();
        this.fetchDriverList();
        this.fetchColorList();
        this.fetchIntroByList();
        this.fetchPaymentMethodList();
        
        // Default order_date is today, won't trigger updated event.
        // When load order data, fetch product list will be triggered when order_date updated.
        // But if trigger by changed event, when order created and edited at same day, the product list won't be fetched.
        // So let it request twitce for now.
        if (this.orderId) {
          this.loadOrderData(this.orderId);
        } else {
          this.resetForm();
        }
        this.fetchProductList(this.formData.order_date);
      }
    },
    'formData.order_date': {
      handler(newVal) {
        this.fetchProductList(newVal);
        this.formData.share_day = this.shareDayCheck(newVal);
      }
    },
    'currentOrderItem.product_name': {
      handler(newVal) {
        if (newVal) {
          const sizeList = this.productList.filter(p => p.name === newVal) || [];

          if (sizeList.length == 1) {
            const size = sizeList[0].size;
            this.currentOrderItem.size = size;
            this.currentOrderItem.product_id = this.productList.find(p => p.name === this.currentOrderItem.product_name && p.size === size)?.id;
          }
          else {
            this.currentOrderItem.product_id = '';
            this.currentOrderItem.size = '';
          }

          this.handleGiftCheckboxChange();
          this.currentOrderItem.quantity = 1;
        }
      }
    },
    'currentOrderItem.size': {
      handler(newVal) {
        if (newVal) {
          this.currentOrderItem.product_id = this.productList.find(p => p.name === this.currentOrderItem.product_name && p.size === newVal)?.id;
          this.currentOrderItem.sum = this.calculateItemSum();
          // When extra_fee changes, maxDiscount also changes, so re-evaluate discount
          if (this.currentOrderItem.discount > this.maxDiscount) {
            this.currentOrderItem.discount = this.maxDiscount;
          }
          this.handleGiftCheckboxChange();
        }
      }
    },
    'currentOrderItem.quantity': {
      handler() {
        this.currentOrderItem.sum = this.calculateItemSum();
      }
    },
    'currentOrderItem.discount': {
      handler(newVal) {
        if (newVal > this.maxDiscount) {
          this.currentOrderItem.discount = this.maxDiscount;
        }
        this.currentOrderItem.sum = this.calculateItemSum();
      }
    },
    'currentOrderItem.extra_fee': {
      handler() {
        // When extra_fee changes, maxDiscount also changes, so re-evaluate discount
        if (this.currentOrderItem.discount > this.maxDiscount) {
          this.currentOrderItem.discount = this.maxDiscount;
        }
        this.currentOrderItem.sum = this.calculateItemSum();
      }
    },
    'currentOrderItem.sum': {
      handler() {
        this.currentOrderItem.ship.fee = this.calculateItemShipFee();
        this.currentOrderItem.ship.sum = this.calculateItemShipSum();
      }
    },
    payments: {
      handler(newPayments) {
        // If a credit card payment is added, force need_invoice to true
        const hasCreditCardPayment = newPayments.some(payment => payment.method_id === this.creditCardMethodId);
        if (hasCreditCardPayment && !this.formData.need_invoice) {
          this.formData.need_invoice = true;
        }
      },
      deep: true
    }
  },
  methods: {
    // Generic Address Autocomplete Methods
    shareDayCheck(newOrderDate) {
      var is_holiday = false;
      if (newOrderDate) {
        const date = new Date(newOrderDate);
        const dayOfWeek = date.getDay(); // 0 for Sunday, 6 for Saturday
        is_holiday = (dayOfWeek === 0 || dayOfWeek === 6);
      }
      return is_holiday;
    },
    handleAddressInput(field) {
      if (this.debounceTimeout[field]) {
        clearTimeout(this.debounceTimeout[field]);
      }
      this.debounceTimeout[field] = setTimeout(() => {
        this.fetchAddressSuggestions(field);
      }, 300); // Debounce
    },
    async fetchAddressSuggestions(field) {
      const input = this.formData[field];
      if (input.length < 2) { // Only fetch if input is at least 2 characters
        this.addressSuggestions[field] = [];
        this.showSuggestions[field] = false;
        return;
      }
      try {
        const response = await fetch(`/api/places/autocomplete?input=${encodeURIComponent(input)}`);
        if (!response.ok) throw new Error('Failed to fetch address suggestions');
        const data = await response.json();
        this.addressSuggestions[field] = data.predictions || [];
        this.showSuggestions[field] = this.addressSuggestions[field].length > 0;
      } catch (error) {
        console.error(`Error fetching address suggestions for ${field}:`, error);
        this.addressSuggestions[field] = [];
        this.showSuggestions[field] = false;
      }
    },
    selectAddressSuggestion(field, suggestion) {
      this.formData[field] = suggestion.description;
      this.addressSuggestions[field] = [];
      this.showSuggestions[field] = false;
    },
    clearSuggestions(field) {
      this.addressSuggestions[field] = [];
      this.showSuggestions[field] = false;
    },
    hideSuggestions(field) {
      // Delay hiding to allow click events on suggestions to register
      setTimeout(() => {
        this.showSuggestions[field] = false;
      }, 150);
    },
    shipFeeCleaned(){
      if (this.currentOrderItem.ship.fee === '' || this.currentOrderItem.ship.fee === null) {
        this.currentOrderItem.ship.is_fixed_fee = false;
        this.currentOrderItem.ship.fee = this.calculateItemShipFee();
        this.currentOrderItem.ship.sum = this.calculateItemShipSum();
      }
      else {
        this.currentOrderItem.ship.is_fixed_fee = true;
        console.log('Fixed fee');
      }
    },
    updateShipFee(event) {
      // Only update if it's a user input event
      if (event instanceof Event) {
        this.$nextTick(() => {
          const newValue = Math.round(this.currentOrderItem.ship.fee) || 0;
          if (newValue > 0) {
            this.currentOrderItem.ship.is_fixed_fee = true;
            this.currentOrderItem.ship.fee = newValue;
          }
          this.currentOrderItem.ship.sum = this.calculateItemShipSum();
        });
      }
    },
    closeForm() {
      this.resetForm();
      this.$emit('close');
    },
    resetForm() {
      this.formData = {
        id: '',
        name: '',
        sales_id: '',
        order_date: new Date().toISOString().split('T')[0],
        deliver_date: '',
        phone: '',
        phone2: '',
        addr: '',
        addr2: '',
        intro_by: '',
        memo: '',
        need_invoice: true,
        delivered: false,
        invoice: '',
        share_day: false,
        is_gift: false,
      };

      this.orderItems = [];
      this.disposals = [];
      this.payments = [];
      this.currentOrderItem = {
        product_id: '',
        size: '',
        quantity: 1,
        discount: 0,
        extra_fee: 0,
        color: '',
        customized: false,
        extra_memo: '',
        is_gift: false,
        ship: {
          driver_id: '',
          share_percent: 0,
          is_fixed_fee: false,
          fee: 0,
          extra_fee: 0,
          extra_memo: '',
          sum: 0
        }
      };
      this.currentDisposal = {
        quantity: 0,
        fee: 350,
        extra_fee: 0,
        ship: {
          driver_id: this.defaultDriver ? this.defaultDriver.id : '',
          is_fixed_fee: true,
          fee: 100,
          extra_fee: 0,
          extra_memo: '',
          sum: 100
        },
        sum: 350
      };
      this.currentPayment = {
        pay_date: new Date().toISOString().split('T')[0],
        method_id: '',
        amount: 0,
        invoice: '',
        series: ''
      };
      this.isSubmitting = false;
      this.submitted = false;
      // Initialize autocomplete related objects for both fields
      this.addressSuggestions = { addr: [], addr2: [] };
      this.showSuggestions = { addr: false, addr2: false };
      this.debounceTimeout = { addr: null, addr2: null };
    },
    async fetchSalesList() {
      try {
        const response = await fetch('/api/sales');
        if (!response.ok) throw new Error('Failed to fetch sales list');
        this.salesList = await response.json();
      } catch (error) {
        console.error('Error fetching sales list:', error);
      }
    },
    async fetchDriverList() {
      try {
        const response = await fetch('/api/drivers');
        if (!response.ok) throw new Error('Failed to fetch driver list');
        this.driverList = await response.json();

        this.defaultDriver = this.driverList.find(d => d.default)
        this.currentOrderItem.ship.driver_id = this.defaultDriver.id
        this.currentOrderItem.ship.share_percent = this.defaultDriver.share_percent
        this.currentDisposal.ship.driver_id = this.defaultDriver.id
      } catch (error) {
        console.error('Error fetching driver list:', error);
      }
    },
    async fetchProductList(date) {
      this.productLoading = true;
      this.productError = null;
      try {
        const response = await fetch(`/api/products/${date}`);
        if (!response.ok) throw new Error('Failed to fetch product list');
        this.productList = await response.json();
        this.giftList = [...new Set(this.productList.filter(p => p.type === 3).map(p => p.name))];
        this.updateOrderItemsAfterProductListChange();
      } catch (error) {
        console.error('Error fetching product list:', error);
        this.productError = 'Failed to load products. Please try again.';
      } finally {
        this.productLoading = false;
      }
    },
    /**
     * Update all orderItems and currentOrderItem after productList changes (e.g. order_date changed)
     * - For each orderItem, update product_id by name/size, recalc sum and ship fee
     * - For currentOrderItem, update product_id by name/size, recalc sum and ship fee
     */
    updateOrderItemsAfterProductListChange() {
      // Update all added order items
      this.orderItems = this.orderItems.map(item => {
        const newProduct = this.productList.find(p => p.name === item.product_name && p.size === item.size);
        if (newProduct) {
          // Update product_id, price, and recalc sum/ship
          const updated = {
            ...item,
            product_id: newProduct.id,
          }
          updated.sum = this.calculateItemSum(updated);
          updated.ship.fee = this.calculateItemShipFee(updated);
          updated.ship.sum = this.calculateItemShipSum(updated);
          return updated;

        } else {
          // Product not found, keep as is (or optionally clear product_id)
          return { ...item, product_id: '' };
        }
      });
      // Update currentOrderItem if it has a name/size
      if (this.currentOrderItem.product_name && this.currentOrderItem.size) {
        const newProduct = this.productList.find(p => p.name === this.currentOrderItem.product_name && p.size === this.currentOrderItem.size);
        if (newProduct) {
          this.currentOrderItem.product_id = newProduct.id;
          this.currentOrderItem.sum = this.calculateItemSum();
          this.currentOrderItem.ship.fee = this.calculateItemShipFee();
          this.currentOrderItem.ship.sum = this.calculateItemShipSum();
        } else {
          this.currentOrderItem.product_id = '';
        }
      }
    },
    async fetchColorList() {
      try {
        const response = await fetch('/api/colors');
        if (!response.ok) throw new Error('Failed to fetch color list');
        this.colorList = await response.json();
      } catch (error) {
        console.error('Error fetching color list:', error);
      }
    },
    async fetchIntroByList() {
      try {
        const response = await fetch('/api/customer_sources');
        if (!response.ok) throw new Error('Failed to fetch intro by list');
        this.introByOptions = await response.json();
      } catch (error) {
        console.error('Error fetching intro by list:', error);
      }
    },
    async fetchPaymentMethodList() {
      try {
        const response = await fetch('/api/payment_methods');
        if (!response.ok) throw new Error('Failed to fetch payment method list');
        this.paymentMethods = await response.json();
      } catch (error) {
        console.error('Error fetching payment method list:', error);
      }
    },
    handleNeedInvoiceChange() {
      // This method is called when the checkbox is clicked/changed
      // If a credit card payment exists and the user tries to uncheck it
      if (this.isNeedInvoiceDisabled && !this.formData.need_invoice) {
        alert('付款使用信用卡，需要開發票');
        // Revert the change
        this.formData.need_invoice = true;
      }
    },
    calculateItemPrice(assigned_item) {
      const item = assigned_item || this.currentOrderItem;
      const selectedProduct = this.productList.find(
        p => p.id === item.product_id
      );
      const discount = parseFloat(item.discount) || 0;
      const extra_fee = parseFloat(item.extra_fee) || 0;
      return selectedProduct ? (selectedProduct.price - discount + extra_fee) : 0;
    },
    calculateItemSum(assigned_item) {
      const item = assigned_item || this.currentOrderItem;    
      return this.calculateItemPrice(item) * (item.quantity || 0);
    },
    calculateItemShipFee(assigned_item) {
      const item = assigned_item || this.currentOrderItem;
      let ship_fee = 0;
      if (item.ship.is_fixed_fee) {
        ship_fee = parseFloat(item.ship.fee) || 0;
      } else {
        const item_price = this.calculateItemPrice(item);
        // Accessory do not have ship fee.
        const p = this.productList.find(p => p.id == item.product_id ? p : null);
        if (p && p.type == 3) {
          return 0;
        }
        const share_percent = item.ship.share_percent || (this.driverList.find(d => d.id === item.ship.driver_id)?.share_percent || 0);
        ship_fee = item_price * share_percent;
      }
      return Math.round(ship_fee);
    },
    calculateItemShipSum(item) {
      const cal_item = item || this.currentOrderItem;
      const ship_fee = this.calculateItemShipFee(cal_item);
      const ship_extra_fee = parseFloat(cal_item.ship.extra_fee) || 0;
      return Math.round(cal_item.quantity * (ship_fee + ship_extra_fee));
    },
    calculateDisposalShipSum(item) {
      const cal_item = item || this.currentDisposal;
      return cal_item.quantity * (cal_item.ship.fee + cal_item.ship.extra_fee);
    },
    calculateDisposalSum(item) {
      const cal_item = item || this.currentDisposal;
      if (!cal_item.quantity || cal_item.quantity === 0) {
        return 0;
      }
      return cal_item.quantity * (cal_item.fee + cal_item.extra_fee);
    },
    calculateUnpaid() {
      const paid = this.payments.reduce((sum, item) => sum + (item.amount || 0), 0);
      const total = this.orderItems.reduce((sum, item) => sum + (item.sum || 0), 0);
      return total - paid;
    },
    handleGiftCheckboxChange() {
      const item = this.currentOrderItem;
      const selectedProduct = this.productList.find(p => p.id === item.product_id);

      if (!this.giftList.includes(item.product_name)) {
        item.is_gift = false;
      }

      if (item.is_gift && selectedProduct) {
        // If checked, set discount = price and add "贈品" to extra_memo
        item.discount = selectedProduct.price + this.currentOrderItem.extra_fee;
        if (!item.extra_memo.includes('贈品')) {
          item.extra_memo = item.extra_memo ? `${item.extra_memo}, 贈品` : '贈品';
        }
      } else {
        // If unchecked, set discount to 0 and remove "贈品" from extra_memo
        item.discount = 0;
        item.extra_memo = item.extra_memo.replace(/,?\s*贈品/g, '').trim();
        if (item.extra_memo.startsWith(',')) {
          item.extra_memo = item.extra_memo.substring(1).trim();
        }
      }
      // Recalculate sum after discount change
      item.sum = this.calculateItemSum(item);
      item.ship.fee = this.calculateItemShipFee(item);
      item.ship.sum = this.calculateItemShipSum(item);
    },
    async checkOrderId() {
      if (!this.formData.id || this.orderId || this.orderIdInvalid) {
        this.orderIdExists = false;
        this.existingOrderDetails = null;
        return;
      }

      try {
        const response = await fetch(`/api/orders/check/${this.formData.id}`);
        if (!response.ok) throw new Error('Failed to check order ID');
        const data = await response.json();
        this.orderIdExists = data.exists;
        this.existingOrderDetails = data.exists ? data : null;

        if (this.orderIdExists) {
          alert(`訂單編號 ${data.order_id} 已存在。\n顧客姓名: ${data.customer_name}\n訂貨日期: ${data.order_date}`);
        }
      } catch (error) {
        console.error('Error checking order ID:', error);
        this.orderIdExists = false;
        this.existingOrderDetails = null;
      }
    },
    getProductDetails(productId) {
      return this.productList.find(p => p.id === productId);
    },
    addOrderItem() {
      if (!this.isValidOrderItem) {
        return;
      }

      const selectedProduct = this.getProductDetails(this.currentOrderItem.product_id);
      if (!selectedProduct) {
        alert('Selected product not found');
        return;
      }

      // Check if product is in stock (if your API provides this info)
      if (selectedProduct.in_stock === false) {
        alert('This product is out of stock');
        return;
      }
      
      const { is_gift, ...newItem } = this.currentOrderItem; // eslint-disable-line no-unused-vars

      newItem.sum = this.calculateItemSum();
      newItem.ship.fee = this.calculateItemShipFee();
      newItem.ship.sum = this.calculateItemShipSum();
      this.orderItems.push(newItem);

      // Reset current order item
      this.currentOrderItem = {
        product_id: '',
        size: '',
        quantity: 1,
        color: '',
        customized: false,
        discount: 0,
        extra_fee: 0,
        extra_cost: 0,
        extra_memo: '',
        is_gift: false,
        ship: {
          driver_id: this.defaultDriver.id,
          share_percent: this.defaultDriver.share_percent,
          is_fixed_fee: false,
          fee: 0,
          extra_fee: 0,
          extra_memo: '',
          sum: 0
        },
        sum: 0
      };
    },
    addDisposalItem() {
      if (!this.validateDisposal()) {
        return;
      }
      const newDisposal = {
        ...this.currentDisposal
      }
      newDisposal.ship.sum = this.calculateDisposalShipSum()
      newDisposal.sum = this.calculateDisposalSum();
      this.disposals.push(newDisposal);

      // Reset current disposal item
      this.currentDisposal = {
        quantity: 0,
        fee: 350,
        extra_fee: 0,
        ship: {
          driver_id: this.defaultDriver.id,
          is_fixed_fee: true,
          fee: 100,
          extra_fee: 0,
          extra_memo: '',
          sum: 100
        },
        sum: 350
      };
    },
    addPaymentItem() {
      if (!this.validatePayment()) {
        return;
      }

      const newPayment = {
        ...this.currentPayment,
      };
      this.payments.push(newPayment);
      this.currentPayment = {
        pay_date: new Date().toISOString().split('T')[0],
        method_id: '',
        amount: 0,
        invoice: '',
        series: ''
      };
    },
    removeOrderItem(index, type = 'order_item') {
      if (type === 'order_item') {
        this.orderItems.splice(index, 1);
      } else if (type === 'disposal') {
        this.disposals.splice(index, 1);
      } else if (type === 'payment') {
        this.payments.splice(index, 1);
      }
    },
    validateDisposal() {
      const errors = [];
      if (!this.currentDisposal.quantity) errors.push('Quantity is required');
      if (!this.currentDisposal.fee) errors.push('Fee is required');
      
      if (errors.length > 0) {
        alert(errors.join('\n'));
        return false;
      }
      return true;
    },
    validatePayment() {
      const errors = [];
      if (!this.currentPayment.pay_date) errors.push('Payment Date is required');
      if (!this.currentPayment.method_id) errors.push('Payment Method is required');
      if (!this.currentPayment.amount) errors.push('Payment Amount is required');
      
      if (errors.length > 0) {
        alert(errors.join('\n'));
        return false;
      }
      return true;
    },
    validateForm() {
      this.submitted = true;
      const errors = [];

      if (!this.formData.id) errors.push('請輸入訂單編號');
      else if (this.orderIdInvalid) errors.push(ORDER_ID_ERROR);
      if (!this.formData.name) errors.push('請輸入客戶姓名');
      if (!this.formData.sales_id) errors.push('請選擇業務員');
      if (!this.formData.order_date) errors.push('請輸入訂單日期');
      if (!this.formData.phone) errors.push('請輸入電話');
      if (!this.formData.addr) errors.push('請輸入地址');
      if (this.orderItems.length === 0) errors.push('請新增至少一項商品');

      if (this.orderIdExists && !this.orderId) {
        errors.push('訂單編號已存在，請修改');
      }

      if (errors.length > 0) {
        alert(errors.join('\n'));
        return false;
      }

      return true;
    },
    async submitForm() {
      // Validate form first
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;

      const url = this.orderId ? `/api/orders/${this.orderId}` : '/api/orders';
      const method = this.orderId ? 'PUT' : 'POST';

      try {
        const response = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...this.formData,
            order_items: this.orderItems,
            disposals: this.disposals,
            payments: this.payments
          })
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const result = await response.json();
        console.log(this.orderId ? 'Order updated:' : 'Order created:', result);
        
        // Reset form
        this.resetForm();
        
        // Emit event to parent
        if (this.orderId) {
          if (this.formData.id !== this.originalOrderId) {
            this.$emit('order-created');
          } else {
            this.$emit('order-updated');
          }
        } else {
          this.$emit('order-created');
        }
        
        // Close the form
        this.closeForm();
        
      } catch (error) {
        console.error(`Error ${this.orderId ? 'updating' : 'creating'} order:`, error);
        alert(`訂單${this.orderId ? '更新' : '建立'}失敗，請稍後再試`);
      } finally {
        this.isSubmitting = false;
      }
    },
    async loadOrderData(orderId) {
      try {
        const response = await fetch(`/api/orders/${orderId}`);
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          throw new Error(`Failed to fetch order data: ${response.status} ${response.statusText}`);
        }
        
        const orderData = await response.json();
        
        // Update form data with order details
        this.formData = {
          id: orderData.id,
          name: orderData.name,
          sales_id: orderData.sales_id,
          delivered: orderData.delivered,
          order_date: orderData.order_date ? orderData.order_date.split('T')[0] : '',
          deliver_date: orderData.deliver_date ? orderData.deliver_date.split('T')[0] : '',
          phone: orderData.phone || '',
          phone2: orderData.phone2 || '',
          addr: orderData.addr || '',
          addr2: orderData.addr2 || '',
          intro_by: orderData.intro_by || '',
          memo: orderData.memo || '',
          need_invoice: orderData.need_invoice || false,
          invoice: orderData.invoice || '',
          share_day: orderData.share_day || false
        };
        
        // Set order items
        if (orderData.items && orderData.items.length > 0) {
          this.orderItems = orderData.items.map(item => {
            const order_item = {
              ...item,
              ship: {
                id: item.ship_id || null,
                fee: item.ship_fee || 0,
                extra_fee: item.ship_extra_fee || 0,
                extra_memo: item.ship_extra_memo || '',
                driver_id: item.driver_id || null,
                driver_name: item.driver_name || '',
                share_percent: item.driver_share_percent || 0,
                is_fixed_fee: item.ship_is_fixed_fee || false,
                sum: item.ship_sum || 0
              },
            };
            return order_item;
          });
        }
        
        // Set disposal items
        if (orderData.disposals && orderData.disposals.length > 0) {
          this.disposals = orderData.disposals.map(disposal => ({
            ...disposal,
            ship: {
              id: disposal.ship_id || null,
              fee: disposal.ship_fee || 0,
              extra_fee: disposal.ship_extra_fee || 0,
              extra_memo: disposal.ship_extra_memo || '',
              driver_id: disposal.driver_id || null,
              driver_name: disposal.driver_name || '',
              sum: disposal.ship_sum || 0
            }
          }));
        }
        
        // Set payment items
        if (orderData.payments && orderData.payments.length > 0) {
          this.payments = orderData.payments;
        }
        
        // Remove keys with prefix "ship_" or "driver_"
        this.orderItems.forEach(item => {
          Object.keys(item).forEach(key => {
            if (key.startsWith('ship_') || key.startsWith('driver_')) {
              delete item[key];
            }
          });
        });
        
        this.disposals.forEach(disposal => {
          Object.keys(disposal).forEach(key => {
            if (key.startsWith('ship_') || key.startsWith('driver_')) {
              delete disposal[key];
            }
          });
        });
        
      } catch (error) {
        console.error('Error loading order data:', error);
        alert(`無法載入訂單資料: ${error.message}`);
        this.closeForm();
      }
    },
    formatCurrency(val) {
      const v = val || 0;
      return new Intl.NumberFormat('zh-TW', {
          style: 'currency',
          currency: 'TWD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
      }).format(v);
    },
  }
};
</script>

<style scoped>
.sections-container {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin: 1rem 0;
}

.sections-container > .form-section {
  flex: 1;
  width: fit-content;
  margin: 0;
  transition: all 0.3s ease;
}

/* Make sections stack vertically on small screens */
@media (max-width: 1600px) {
  .sections-container {
    flex-direction: column;
  }
  
  .sections-container > .form-section {
    width: fit-content;
    min-width: 100%;
  }
}

.form-section {
  margin-bottom: 1.5rem;
}

.form-container h2 {
  text-align: center;
}

.order-information {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.order-information h3 {
  text-align: left;
  margin-bottom: 20px;
}
.form-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.form-container {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 100%;
  max-width: fit-content;
  position: relative;
  margin: 20px;
  max-height: 90vh;
  overflow-y: auto;
}

@media (max-width: 1600px) {
  .form-container {
    max-width: 95vw; /* Allow content to overflow and enable horizontal scroll */
    overflow-x: auto;
  }
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 5px;
}

.form-sections {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
}

.order-information .form-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-bottom: 15px;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.order-information .form-group {
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.form-row .form-group {
  flex: 1;
  min-width: 0;
  margin-right: 15px;
}

/* Checkbox group styles */
.checkbox-group {
  display: flex;
  gap: 20px;
  align-items: center;
  padding: 8px 0;
  flex-wrap: wrap;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  margin: 4px 0;
}

.checkbox-item input[type="checkbox"] {
  margin: 0;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.checkbox-item label {
  margin: 0;
  cursor: pointer;
  user-select: none;
}



.form-group.has-error input,
.form-group.has-error select,
.form-group.has-error textarea {
  border-color: #dc3545;
}

.error-message {
  color: #dc3545;
  font-size: 0.8em;
  margin-top: 4px; /* Adjust spacing from the input field */
  /* Removed absolute positioning */
}

h2 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #2c3e50;
}

.form-section {
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.form-section h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #2c3e50;
  font-size: 1.1em;
}

.form-group {
  margin-bottom: 15px;
}

.form-group.checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-group.checkbox input {
  width: auto;
}

.form-group.checkbox label {
  margin: 0;
}

label {
  display: block;
  font-weight: bold;
}

input,
textarea,
select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
}

textarea {
  resize: vertical;
}

/* Styles for Address Autocomplete */
.address-autocomplete-container {
  position: relative;
}

.address-suggestions-list {
  position: absolute;
  top: 100%; /* Position below the input */
  left: 0;
  right: 0;
  z-index: 100;
  background-color: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  list-style: none;
  padding: 0;
  margin: 0;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.address-suggestions-list li {
  padding: 10px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
}

.address-suggestions-list li:last-child {
  border-bottom: none;
}

.address-suggestions-list li:hover {
  background-color: #f0f0f0;
}

.form-actions {
  display: flex;
  gap: 10px;
  padding: 0 15px;
}

.submit-btn,
.cancel-btn {
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  flex: 1;
  transition: background-color 0.3s;
}

.submit-btn {
  background-color: #2c3e50;
  color: white;
  border: none;
}

.submit-btn:hover:not(:disabled) {
  background-color: #1a252f;
}

.submit-btn:disabled {
  background-color: #95a5a6;
  cursor: not-allowed;
}

.cancel-btn {
  background-color: #95a5a6;
  color: white;
  border: none;
}

.cancel-btn:hover {
  background-color: #7f8c8d;
}

.product-list-container {
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 15px; /* Re-add padding */
  width: fit-content; /* Allow container to expand with content */
  min-width: 100%; /* Ensure it takes at least 100% of parent width */
  overflow-x: auto; /* Add horizontal scroll to the container */
}

.product-list {
  /* width: 100%; */
  width: max-content; /* Allow table to expand to its content's natural width */
  min-width: 100%; /* Ensure it takes at least 100% of parent width */
  border-collapse: collapse;
}

.product-list th, 
.product-list td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

/* Quantity column styling */
.quantity-column {
  width: 50px;  /* Fixed width to fit 2 digits */
  min-width: 50px;
  max-width: 50px;
  text-align: center;
}

.price-column {
  /* width: 100px; */
  min-width: 80px;
  max-width: 80px;
  text-align: left;
}

.name-column {
  min-width: 80px;
  max-width: 100px;
}

.date-column {
  min-width: 120px;
  max-width: 130px;
}

.size-column {
  min-width: 70px;
  max-width: 140px;
}

.payment-method-column {
  min-width: 120px;
  max-width: 120px;
}

.product-name-column {
  min-width: 240px;
  max-width: 260px;
}

.product-name-flex-container {
  display: flex;
  align-items: center;
}

.gift-colmun {
  display: flex;
  align-items: center;
  white-space: nowrap; /* Prevent text from wrapping */
  min-width: 60px; /* Increased width to prevent vertical stacking */
  border: 0px;
}

.checkbox-gift {
  width: 12px !important;
  margin: 5px;
}

.customize-column {
  min-width: 18px;
  max-width: 18px;
}

.info-column {
  min-width: 120px;
  max-width: 130px;
}

.add-btn-column {
  min-width: 50px;
  max-width: 50px;
}

.memo-column {
  min-width: 70px;
  max-width: 100px;
}

.product-list .new-product-input input,
.product-list .new-product-input select,
.product-list .new-disposal-input input,
.product-list .new-disposal-input select,
.product-list .new-payment-input input,
.product-list .new-payment-input select {
  width: 100%;
  box-sizing: border-box;
}

.product-list .total-row {
  font-weight: bold;
  background-color: #f1f3f5;
}

.product-list .total-label,
.product-list .total-ship-label {
  text-align: right;
  padding-right: 10px;
}

.product-list .total-value {
  min-width: 40px;
  text-align: left;
  font-weight: bold;
}

.add-btn {
  background-color: #4CAF50;
  color: white;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.add-btn:hover {
  background-color: #45a049;
}

.add-btn:disabled,
.remove-btn:disabled {
  background-color: #95a5a6;
  cursor: not-allowed;
}

.remove-btn {
  background-color: #f44336;
  color: white;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.remove-btn:hover {
  background-color: #d32f2f;
}

.form-group.checkbox input[type="checkbox"] {
  width: auto;
  margin-right: 8px;
}

@media (max-width: 768px) {
  .order-information .form-row {
    grid-template-columns: 1fr;
  }
  
  .form-container {
    margin: 10px;
    max-height: 90vh;
    overflow-y: auto;
  }
  
  .add-product,
  .add-disposal {
    margin-top: 20px;
  }
  
  .add-disposal .product-list th,
  .add-disposal .product-list td {
    padding: 8px;
  }
}

/* Add styles for disabled options */
select option:disabled {
  color: #999;
  font-style: italic;
}

/* Add loading state styles */
.product-loading {
  opacity: 0.7;
  pointer-events: none;
}

.error-message {
  color: #dc3545;
  font-size: 0.8em;
  margin-top: 4px;
}
</style>
